import { styled } from '@precis-digital/kurama/utils';
import { type ThemeColor } from '@precis-digital/kurama/theme';
import { type ReactElement } from 'react';
import AlertSymbol from 'shared/components/Alert/AlertSymbol';
import ChartBadge from '../components/ChartBadge';
import { TOOLTIP_OUT_OF_ZOOM_OPACITY } from '../constants';
import type { ChartCardDefaults, ChartCardTFunction, ChartChoiceOption, ChartChoiceOptions, ChartDataRow, ChartSelections, ChartType, DrawingColorMaps } from '../types';
import type { AxisMetricsTypes, DimensionOrMetricDomain, ElementDescriptionDimension, ElementDescriptionDimensionChoice, ElementDescriptionMetric, ElementDescriptionMetricChoice, ElementsDescriptionsMap, FirstOrThirdAxisDetails, MetricDomain, RealTooltipPayload, SecondAxisChoiceValuesType, SecondAxisDetails, TooltipBodyElementKeyContent } from '../internalTypes';
import { getSelectedOtherOptions } from './chart';
import { errorLocal, generateIdForSelectionOthers, getDimensionLabel, isSelectionOthersId, formatDateValue, formatMetricValue, isNullDimensionValue } from './generics';
export const getTooltipTitle = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  payload,
  firstAxisDetails,
  elementsDescriptionsMap,
  thirdAxisDetails,
  selections,
  t
}: {
  payload: Array<RealTooltipPayload<DataKey, DataRow>>;
  firstAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  thirdAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow> | undefined;
  selections: ChartSelections<DataKey>;
  t: ChartCardTFunction;
}): string => {
  const {
    axis,
    dataKey,
    isDate
  } = thirdAxisDetails ?? firstAxisDetails;
  const value = payload?.[0]?.payload?.[dataKey] as string;
  const {
    descriptionsOfMap
  } = elementsDescriptionsMap[dataKey] as ElementDescriptionDimensionChoice;

  // The tooltip is the explaining surface for the '-' shown on the axis, legend and table.
  if (isNullDimensionValue(value)) {
    return t('noData');
  }
  if (isDate) {
    return formatDateValue(dataKey, (elementsDescriptionsMap[dataKey] ?? elementsDescriptionsMap[axis]) as ElementDescriptionDimension, value);
  }
  const dimensionDescription = (elementsDescriptionsMap[dataKey] ?? elementsDescriptionsMap[axis]) as ElementDescriptionDimension;
  if (descriptionsOfMap != null) {
    return descriptionsOfMap[selections[dataKey] as string]?.[value]?.label ?? getDimensionLabel(dimensionDescription, value, dataKey);
  }
  return getDimensionLabel(dimensionDescription, value, dataKey);
};
export const getTooltipBodyElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  chartType,
  payload,
  firstAxisDetails,
  firstAxisDomain,
  secondAxisDetails,
  secondAxisDomain,
  otherSecondAxisDomain,
  elementsDescriptionsMap,
  drawingColorMaps,
  selections,
  t
}: {
  chartType: ChartType;
  payload: Array<RealTooltipPayload<DataKey, DataRow>>;
  firstAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
  firstAxisDomain: DimensionOrMetricDomain;
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  secondAxisDomain: MetricDomain;
  otherSecondAxisDomain: MetricDomain;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  drawingColorMaps: DrawingColorMaps;
  selections: ChartSelections<DataKey>;
  t: ChartCardTFunction;
  defaults: ChartCardDefaults;
}): ReactElement[] => {
  if (payload.length === 0) {
    return [];
  }
  const bodyElements: TooltipBodyElementKeyContent[] = [];
  if (chartType === 'scatter') {
    const {
      axis,
      dataKey: firstAxisDataKey,
      label: firstAxisLabel
    } = firstAxisDetails;
    createAxisMetricsSectionElements<DataKey, DataRow>({
      defaults,
      axis,
      axisLabel: firstAxisLabel,
      chartType,
      metricType: 'metric',
      dataKeys: [firstAxisDataKey],
      choiceValues: undefined,
      otherSelected: undefined,
      selectedOthers: undefined,
      selectedValues: undefined,
      t,
      payload,
      selections,
      elementsDescriptionsMap,
      drawingColorMaps,
      axisDomain: firstAxisDomain as MetricDomain,
      bodyElements
    });
  }
  for (const {
    axis,
    axisType,
    label: axisLabel,
    chartType,
    type: metricType,
    dataKeys,
    choiceValues,
    otherSelected,
    selectedOthers,
    selectedValues
  } of secondAxisDetails) {
    createAxisMetricsSectionElements<DataKey, DataRow>({
      defaults,
      axis,
      axisLabel,
      chartType,
      metricType,
      dataKeys,
      choiceValues,
      otherSelected,
      selectedOthers,
      selectedValues,
      t,
      payload,
      selections,
      elementsDescriptionsMap,
      drawingColorMaps,
      axisDomain: axisType === 'second' ? secondAxisDomain : otherSecondAxisDomain,
      bodyElements
    });
  }
  return bodyElements.map(([key, content]) => <StyledTooltipPayloadItem key={key}>{content}</StyledTooltipPayloadItem>);
};
interface GenerateTooltipPayloadElementProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  defaults: ChartCardDefaults;
  t: ChartCardTFunction;
  chartType: ChartType;
  selectedOthers?: boolean;
  otherOptions: ChartChoiceOptions<string> | undefined;
  firstOtherOption: ChartChoiceOption<string> | undefined;
  otherOptionDimension: DataKey | undefined;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  drawingColorMaps: DrawingColorMaps;
  singlePayload: RealTooltipPayload<DataKey, DataRow>;
  axisDomain: MetricDomain;
  axisLabel?: string;
  bodyElements: TooltipBodyElementKeyContent[];
}
const generateTooltipPayloadBodyElement = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  t,
  chartType,
  selectedOthers,
  otherOptions,
  firstOtherOption,
  otherOptionDimension,
  elementsDescriptionsMap,
  drawingColorMaps,
  singlePayload,
  axisDomain,
  axisLabel,
  bodyElements
}: GenerateTooltipPayloadElementProps<DataKey, DataRow>): void => {
  const {
    dataKey,
    value
  } = singlePayload;
  const color = singlePayload[drawingColorMaps.primary[chartType]];
  let element: DataKey = dataKey;
  let othersLabel: string | undefined;
  if (isSelectionOthersId(dataKey)) {
    element = dataKey.replace(generateIdForSelectionOthers(otherOptionDimension as DataKey), (firstOtherOption as ChartChoiceOption<string>).value) as DataKey;
    othersLabel = t('others');
  }
  const elementDescription = elementsDescriptionsMap[element] as ElementDescriptionMetric;
  if (elementDescription == null) {
    errorLocal(`DefaultTooltipContent generateTooltipPayloadElements elementDescription null`, {
      elementsDescriptionsMap,
      element,
      otherOptions,
      selectedOthers,
      dataKey
    });
    return;
  }
  const formattedValue = formatMetricValue(dataKey, elementDescription, value, defaults.tooltip.format);
  const outOfZoom = axisDomain != null && typeof value === 'number' && typeof axisDomain[0] === 'number' && typeof axisDomain[1] === 'number' && (value < axisDomain[0] || value > axisDomain[1]);
  let itemLabel = othersLabel ?? elementDescription.label ?? dataKey;
  if (axisLabel != null && axisLabel !== itemLabel) {
    itemLabel = `${axisLabel} (${itemLabel})`;
  }
  bodyElements.push([dataKey, [<StyledTooltipPayloadItem key={dataKey}>
        <StyledTooltipPayloadItemBadgeLabel>
          {chartType !== 'scatter' && <ChartBadge color={color as ThemeColor} key={dataKey} chartType={chartType} pattern={elementDescription.pattern} />}
          <StyledTooltipPayloadItemLabel key="label" outOfZoom={outOfZoom}>
            {itemLabel}
          </StyledTooltipPayloadItemLabel>
          <AlertSymbol alert={elementDescription.alert} width="1em" height="1em" key="alert" />
        </StyledTooltipPayloadItemBadgeLabel>
        <StyledTooltipPayloadItemValue outOfZoom={outOfZoom}>{formattedValue}</StyledTooltipPayloadItemValue>
      </StyledTooltipPayloadItem>]]);
};
const getSortedPayload = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(dataKeys: DataKey[], payload: Array<RealTooltipPayload<DataKey, DataRow>>, chartType: ChartType): Array<RealTooltipPayload<DataKey, DataRow>> => {
  if (chartType === 'area') {
    return [...payload].reverse();
  }
  if (chartType === 'scatter') {
    return dataKeys.map(dataKey => payload.find(p => p.dataKey === dataKey) ?? {
      ...payload[0],
      dataKey,
      value: (payload[0].payload?.[dataKey] ?? null) as string | number | null
    });
  }
  return payload;
};
interface GenerateTooltipPayloadElementsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  defaults: ChartCardDefaults;
  t: ChartCardTFunction;
  chartType: ChartType;
  dataKeys: DataKey[];
  choiceValues?: SecondAxisChoiceValuesType<DataKey, DataRow>;
  selectedValues?: string[];
  otherSelected?: DataKey;
  selectedOthers?: boolean;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  drawingColorMaps: DrawingColorMaps;
  payload: Array<RealTooltipPayload<DataKey, DataRow>>;
  axisDomain: MetricDomain;
  axisLabel?: string;
  bodyElements: TooltipBodyElementKeyContent[];
}
const generateTooltipPayloadBodyElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  t,
  chartType,
  dataKeys,
  choiceValues,
  selectedValues,
  otherSelected,
  selectedOthers,
  elementsDescriptionsMap,
  drawingColorMaps,
  payload,
  axisDomain,
  axisLabel,
  bodyElements
}: GenerateTooltipPayloadElementsProps<DataKey, DataRow>): void => {
  const otherOptions = getSelectedOtherOptions<DataKey, DataRow>(choiceValues, selectedValues, otherSelected, selectedOthers);
  const firstOtherOption = otherOptions?.[0];
  const otherOptionDimension = otherSelected ?? choiceValues?.dimension;
  if (selectedOthers === true && otherOptions == null) {
    errorLocal(`DefaultTooltipContent generateTooltipPayloadElements selectedOthers true and otherOptions null`, {
      selectedOthers,
      choiceValues,
      selectedValues
    });
    return;
  }
  const sortedPayload = getSortedPayload(dataKeys, payload, chartType);
  for (const singlePayload of sortedPayload) {
    if (chartType === 'area' && singlePayload.stroke === 'none') {
      continue;
    }
    if (!dataKeys.includes(singlePayload.dataKey)) {
      continue;
    }
    generateTooltipPayloadBodyElement<DataKey, DataRow>({
      defaults,
      t,
      chartType,
      selectedOthers,
      otherOptions,
      firstOtherOption,
      otherOptionDimension,
      elementsDescriptionsMap,
      drawingColorMaps,
      singlePayload,
      axisDomain,
      axisLabel,
      bodyElements
    });
  }
};
const createAxisMetricsSectionElements = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  axis,
  axisLabel,
  chartType,
  metricType,
  dataKeys,
  choiceValues,
  otherSelected,
  selectedOthers,
  selectedValues,
  t,
  payload,
  selections,
  elementsDescriptionsMap,
  drawingColorMaps,
  axisDomain,
  bodyElements
}: {
  defaults: ChartCardDefaults;
  axis: DataKey;
  axisLabel: string;
  chartType: ChartType;
  metricType: AxisMetricsTypes;
  dataKeys: DataKey[];
  choiceValues: SecondAxisChoiceValuesType<DataKey, DataRow> | undefined;
  otherSelected: DataKey | undefined;
  selectedOthers: boolean | undefined;
  selectedValues: string[] | undefined;
  t: ChartCardTFunction;
  payload: Array<RealTooltipPayload<DataKey, DataRow>>;
  selections: ChartSelections<DataKey>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  drawingColorMaps: DrawingColorMaps;
  axisDomain: MetricDomain;
  bodyElements: TooltipBodyElementKeyContent[];
}): void => {
  const displayAxisLabel = metricType === 'metricVariants' || metricType === 'metricVariantsTransposed' || metricType === 'metricTransposed';
  if (displayAxisLabel) {
    const selected = selections[axis];
    let variant: string | undefined;
    if (typeof selected === 'string') {
      if ((elementsDescriptionsMap[selected as DataKey] as ElementDescriptionMetric).variant != null) {
        variant = (elementsDescriptionsMap[axis] as ElementDescriptionMetricChoice).variant;
      }
    } else {
      variant = (elementsDescriptionsMap[axis] as ElementDescriptionMetricChoice).variant;
    }
    let elementLabel = axisLabel;
    if (variant != null && metricType !== 'metricVariantsTransposed') {
      const selectedVariantValue = selections[variant as DataKey] as string;
      const {
        descriptions: variantDescriptions
      } = elementsDescriptionsMap[variant as DataKey] as ElementDescriptionDimension;
      const variantDescription = variantDescriptions?.[selectedVariantValue as DataKey];
      if (variantDescription != null) {
        elementLabel = `${elementLabel} (${variantDescription.label})`;
      }
    }
    bodyElements.push([axis, elementLabel]);
  }
  generateTooltipPayloadBodyElements<DataKey, DataRow>({
    defaults,
    t,
    chartType,
    dataKeys,
    choiceValues,
    selectedValues,
    otherSelected,
    selectedOthers,
    elementsDescriptionsMap,
    drawingColorMaps,
    payload,
    axisDomain,
    axisLabel: displayAxisLabel ? undefined : axisLabel,
    bodyElements
  });
};
const StyledTooltipPayloadItem = styled('div')(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: '326px',
  width: '100%',
  justifyContent: 'space-between',
  gap: theme.spacing(5)
}));
const StyledTooltipPayloadItemBadgeLabel = styled('div')(({
  theme
}) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
const StyledTooltipPayloadItemLabel = styled('div', {
  shouldForwardProp: prop => prop !== 'outOfZoom'
})<{
  outOfZoom?: boolean;
}>(({
  outOfZoom
}) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  opacity: outOfZoom === true ? TOOLTIP_OUT_OF_ZOOM_OPACITY : 1
}));
const StyledTooltipPayloadItemValue = styled('div', {
  shouldForwardProp: prop => prop !== 'outOfZoom'
})<{
  outOfZoom?: boolean;
}>(({
  theme,
  outOfZoom
}) => ({
  marginLeft: 'auto',
  marginRight: theme.spacing(1),
  whiteSpace: 'nowrap',
  opacity: outOfZoom === true ? TOOLTIP_OUT_OF_ZOOM_OPACITY : 1
}));